import { Controller } from "@hotwired/stimulus"
import { computePosition, flip, shift, offset } from "@floating-ui/dom"

// Connects to data-controller="autocomplete"
export default class extends Controller {
  static targets = ["input", "results", "suggestion"]
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    debounce: { type: Number, default: 300 },
    placement: { type: String, default: "bottom-start" },
    offsetDistance: { type: Number, default: 8 },
    closeOnSelect: { type: Boolean, default: true }
  }

  connect() {
    this.selectedIndex = -1
    this.abortController = null
    this.timeout = null
    this.isOpen = false
    
    // Bind methods to maintain context
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
  }

  disconnect() {
    this.close()
    if (this.abortController) {
      this.abortController.abort()
    }
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  // Handle input events
  onInput(event) {
    const query = event.target.value.trim()

    if (query.length < this.minLengthValue) {
      this.close()
      return
    }

    // Clear existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.search(query)
    }, this.debounceValue)
  }

  // Perform the search
  async search(query) {
    if (!this.urlValue) {
      console.error("Autocomplete: url value is required")
      return
    }

    // Abort previous request if still pending
    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()
    
    try {
      this.showLoading()
      
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.append("q", query)

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.displayResults(data)
    } catch (error) {
      if (error.name === "AbortError") {
        // Request was aborted, ignore
        return
      }
      console.error("Autocomplete search error:", error)
      this.showError()
    }
  }

  // Display results
  displayResults(data) {
    if (!data || data.length === 0) {
      this.showEmpty()
      return
    }

    const html = data.map((item, index) => {
      return `
        <div class="autocomplete-suggestion ${index === 0 ? 'selected' : ''}" 
             data-autocomplete-target="suggestion"
             data-index="${index}"
             data-id="${item.data?.id || ''}"
             data-action="click->autocomplete#select mouseenter->autocomplete#highlight"
             role="option"
             aria-selected="${index === 0 ? 'true' : 'false'}">
          <div class="autocomplete-suggestion-content">
            <span class="autocomplete-suggestion-title">${this.escapeHtml(item.title)}</span>
          </div>
        </div>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
    this.selectedIndex = 0
    this.open()
  }

  // Show loading state
  showLoading() {
    this.resultsTarget.innerHTML = `
      <div class="autocomplete-loading">
        <span>Loading...</span>
      </div>
    `
    this.open()
  }

  // Show error state
  showError() {
    this.resultsTarget.innerHTML = `
      <div class="autocomplete-error">
        <span>Unable to load results</span>
      </div>
    `
    this.open()
  }

  // Show empty state
  showEmpty() {
    this.resultsTarget.innerHTML = `
      <div class="autocomplete-empty">
        <span>No results found</span>
      </div>
    `
    this.open()
  }

  // Open dropdown
  open() {
    if (this.isOpen) return
    
    this.isOpen = true
    this.resultsTarget.classList.remove("hidden")
    this.updatePosition()
    
    // Add event listeners
    document.addEventListener("click", this.handleClickOutside)
    document.addEventListener("keydown", this.handleDocumentKeydown)
  }

  // Close dropdown
  close() {
    if (!this.isOpen) return
    
    this.isOpen = false
    this.resultsTarget.classList.add("hidden")
    this.selectedIndex = -1
    
    // Remove event listeners
    document.removeEventListener("click", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleDocumentKeydown)
  }

  // Update dropdown position
  async updatePosition() {
    if (!this.hasResultsTarget) return

    const { x, y } = await computePosition(this.inputTarget, this.resultsTarget, {
      placement: this.placementValue,
      middleware: [
        offset(this.offsetDistanceValue),
        flip(),
        shift({ padding: 8 })
      ]
    })

    Object.assign(this.resultsTarget.style, {
      left: `${x}px`,
      top: `${y}px`,
      // Match the dropdown width to the input that triggered it, so a wide input
      // (e.g. the admin Create Team slideover) gets a full-width result list rather
      // than the min-width fallback. minWidth: 0 lets this explicit width win over
      // the .autocomplete-results min-width for narrow inputs too.
      width: `${this.inputTarget.offsetWidth}px`,
      minWidth: "0"
    })
  }

  // Handle keyboard navigation
  onKeydown(event) {
    if (!this.isOpen) return

    const suggestions = this.suggestionTargets

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, suggestions.length - 1)
        this.updateSelection()
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection()
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && suggestions[this.selectedIndex]) {
          this.selectSuggestion(suggestions[this.selectedIndex])
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  // Handle document keydown (for Escape when results are open)
  handleDocumentKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  // Update selection highlight
  updateSelection() {
    this.suggestionTargets.forEach((suggestion, index) => {
      if (index === this.selectedIndex) {
        suggestion.classList.add("selected")
        suggestion.setAttribute("aria-selected", "true")
        suggestion.scrollIntoView({ block: "nearest" })
      } else {
        suggestion.classList.remove("selected")
        suggestion.setAttribute("aria-selected", "false")
      }
    })
  }

  // Highlight on hover
  highlight(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.selectedIndex = index
    this.updateSelection()
  }

  // Select a suggestion
  select(event) {
    this.selectSuggestion(event.currentTarget)
  }

  selectSuggestion(suggestionElement) {
    const title = suggestionElement.querySelector(".autocomplete-suggestion-title").textContent
    const id = suggestionElement.dataset.id

    // Dispatch custom event with selection data
    this.dispatch("select", {
      detail: {
        title: title,
        id: id,
        element: suggestionElement
      }
    })

    if (this.closeOnSelectValue) {
      this.close()
    }
  }

  // Handle clicks outside
  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  // Helper to escape HTML
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

